import {
  AgXToneMapping,
  BackSide,
  Box3,
  BoxGeometry,
  type BufferGeometry,
  Color,
  DirectionalLight,
  Group,
  HemisphereLight,
  type Material,
  Mesh,
  MeshLambertMaterial,
  MeshStandardMaterial,
  NoToneMapping,
  type Object3D,
  PMREMGenerator,
  Scene,
  Vector3,
  VSMShadowMap,
  type WebGLRenderer
} from "three";

export type FactoryLightingRig = {
  dispose: () => void;
  refit: (target: Object3D) => void;
};

export type FactoryLightingOptions = {
  environmentAndToneMapping?: boolean;
  lights?: boolean;
  shadows?: boolean;
};

const environmentBlur = 0.02;
const factoryBackground = "#070a0d";

export function createFactoryLighting(
  scene: Scene,
  renderer: WebGLRenderer,
  target: Object3D,
  { environmentAndToneMapping = true, lights = true, shadows = true }: FactoryLightingOptions = {}
): FactoryLightingRig {
  renderer.toneMapping = environmentAndToneMapping ? AgXToneMapping : NoToneMapping;
  renderer.toneMappingExposure = environmentAndToneMapping ? 1.12 : 1;
  renderer.shadowMap.enabled = lights && shadows;
  renderer.shadowMap.type = VSMShadowMap;

  scene.background = new Color(factoryBackground);

  const environmentTarget = environmentAndToneMapping
    ? createFactoryEnvironmentTarget(renderer)
    : undefined;
  scene.environment = environmentTarget?.texture ?? null;
  scene.environmentIntensity = environmentAndToneMapping ? 1.25 : 1;

  const lightRoot = new Group();
  lightRoot.name = "factory-lighting";

  const fillLight = new HemisphereLight("#dce8f2", "#423b34", 0.5);
  fillLight.name = "factory-fill-light";

  const shadowLight = new DirectionalLight("#f2f6ff", 1.7);
  shadowLight.name = "factory-shadow-light";
  shadowLight.castShadow = shadows;
  shadowLight.shadow.mapSize.set(4096, 4096);
  shadowLight.shadow.bias = -0.00015;
  shadowLight.shadow.normalBias = 0.025;
  shadowLight.shadow.intensity = 0.16;
  shadowLight.shadow.radius = 8;
  shadowLight.shadow.blurSamples = 25;

  lightRoot.add(fillLight, shadowLight, shadowLight.target);
  if (lights) {
    scene.add(lightRoot);
  }

  const refit = (nextTarget: Object3D) => {
    if (!lights) {
      return;
    }
    nextTarget.updateWorldMatrix(true, true);
    const bounds = new Box3().setFromObject(nextTarget);

    if (bounds.isEmpty()) {
      return;
    }

    const center = bounds.getCenter(new Vector3());
    const size = bounds.getSize(new Vector3());
    const length = Math.max(size.x, 1);
    const depth = Math.max(size.z, 0.8);
    const height = Math.max(size.y, 1);
    const shadowHeight = bounds.max.y + Math.max(height * 0.8, 3);
    const shadowSpan = Math.max(length, depth) + Math.max(height * 0.8, 3);
    const shadowCamera = shadowLight.shadow.camera;

    shadowLight.position.set(center.x + 0.05, shadowHeight, center.z + 0.06);
    shadowLight.target.position.copy(center);
    shadowCamera.left = -shadowSpan / 2;
    shadowCamera.right = shadowSpan / 2;
    shadowCamera.top = shadowSpan / 2;
    shadowCamera.bottom = -shadowSpan / 2;
    shadowCamera.near = 0.1;
    shadowCamera.far = shadowHeight - bounds.min.y + height + 2;
    shadowLight.target.updateMatrixWorld();
    shadowCamera.updateProjectionMatrix();
  };

  refit(target);

  return {
    dispose() {
      scene.remove(lightRoot);
      if (environmentTarget && scene.environment === environmentTarget.texture) {
        scene.environment = null;
      }
      environmentTarget?.dispose();
    },
    refit
  };
}

function createFactoryEnvironmentTarget(renderer: WebGLRenderer) {
  const environmentScene = createFactoryEnvironment();
  const pmremGenerator = new PMREMGenerator(renderer);
  const environmentTarget = pmremGenerator.fromScene(environmentScene, environmentBlur);
  pmremGenerator.dispose();
  disposeSceneMeshes(environmentScene);
  return environmentTarget;
}

function createFactoryEnvironment() {
  const scene = new Scene();
  const geometry = new BoxGeometry();
  const room = new Mesh(
    geometry,
    new MeshStandardMaterial({
      color: "#3a4147",
      roughness: 1,
      side: BackSide
    })
  );
  room.position.y = 8;
  room.scale.set(36, 20, 28);
  scene.add(room);

  const ceilingLightMaterial = new MeshLambertMaterial({
    color: "#000000",
    emissive: "#eef5ff",
    emissiveIntensity: 28
  });

  [-8, 0, 8].forEach((x) => {
    [-5, 5].forEach((z) => {
      const lightPanel = new Mesh(geometry, ceilingLightMaterial);
      lightPanel.position.set(x, 16, z);
      lightPanel.scale.set(5.5, 0.08, 0.55);
      scene.add(lightPanel);
    });
  });

  const wallBounceMaterial = new MeshLambertMaterial({
    color: "#000000",
    emissive: "#90a9bd",
    emissiveIntensity: 14
  });
  [-1, 1].forEach((side) => {
    [-7, 0, 7].forEach((z) => {
      const wallBounce = new Mesh(geometry, wallBounceMaterial);
      wallBounce.position.set(17 * side, 7, z);
      wallBounce.scale.set(0.08, 6, 1.1);
      scene.add(wallBounce);
    });
  });

  const aisleBounceMaterial = new MeshLambertMaterial({
    color: "#000000",
    emissive: "#f3f7ff",
    emissiveIntensity: 10
  });
  [-1, 1].forEach((side) => {
    [-8, 0, 8].forEach((x) => {
      const aisleBounce = new Mesh(geometry, aisleBounceMaterial);
      aisleBounce.position.set(x, 7, 13 * side);
      aisleBounce.scale.set(1.3, 5, 0.08);
      scene.add(aisleBounce);
    });
  });

  return scene;
}

function disposeSceneMeshes(scene: Scene) {
  const geometries = new Set<BufferGeometry>();
  const materials = new Set<Material>();

  scene.traverse((child) => {
    if (!(child instanceof Mesh)) {
      return;
    }

    geometries.add(child.geometry);
    if (Array.isArray(child.material)) {
      child.material.forEach((material) => {
        materials.add(material);
      });
    } else {
      materials.add(child.material);
    }
  });

  geometries.forEach((geometry) => {
    geometry.dispose();
  });
  materials.forEach((material) => {
    material.dispose();
  });
}
